import React, { useState, useEffect } from 'react';
import axios from 'axios';
import StaffSidebarLayout from './StaffSidebarLayout';

function MultiLocationRequest() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [locations, setLocations] = useState([]);
  const [currentLocationId, setCurrentLocationId] = useState(null);
  const [requestStatus, setRequestStatus] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [requestNote, setRequestNote] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [approvedLocations, setApprovedLocations] = useState([]);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      
      // Fetch current request status
      const statusResponse = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/multi-location/my-request`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Handle null/undefined status - treat as 'none'
      const status = statusResponse.data.requestStatus || 'none';
      setRequestStatus(status);
      // If approved, fetch approved locations
      if (status === 'approved') {
        const approvedResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/multi-location/approved-locations`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setApprovedLocations(approvedResponse.data.locations || []);
      }
      
      // Fetch staff info to get business ID
      const staffResponse = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/staff/me`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Fetch all locations for this business (professional-accessible endpoint)
      const locationsResponse = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/staff/business-locations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const fetchedLocations = locationsResponse.data.locations || [];
      const currentLocId = locationsResponse.data.currentLocationId;
      
      setLocations(fetchedLocations);
      setCurrentLocationId(currentLocId);
      
      // Auto-select current location if no request yet
      if ((status === 'none' || status === 'denied') && currentLocId) {
        setSelectedLocations([currentLocId]);
      }
      
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to load multi-location information');
      // Set default status on error so form shows
      if (!requestStatus) {
        setRequestStatus('none');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!hasAcceptedTerms) {
      setError('Please accept the terms and responsibilities before submitting');
      return;
    }
    
    if (selectedLocations.length === 0) {
      setError('Please select at least one location');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('ogestyle_token');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/multi-location/request`,
        {
          requestedLocationIds: selectedLocations,
          note: requestNote
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess('Multi-location request submitted successfully! Your business owner will review it.');
      setRequestStatus('pending');
      setSelectedLocations([]);
      setRequestNote('');
      
      // Refresh data after 2 seconds
      setTimeout(() => {
        fetchData();
        setSuccess('');
      }, 2000);
      
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleLocationSelection = (locationId) => {
    // Prevent deselecting current location
    if (locationId === currentLocationId && selectedLocations.includes(locationId)) {
      setError('You cannot deselect your current location');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    setSelectedLocations(prev => {
      if (prev.includes(locationId)) {
        return prev.filter(id => id !== locationId);
      } else {
        return [...prev, locationId];
      }
    });
  };

  if (loading) {
    return (
      <StaffSidebarLayout>
        <div className="staff-profile-container">
          <p>Loading...</p>
        </div>
      </StaffSidebarLayout>
    );
  }

  return (
    <StaffSidebarLayout>
      <div className="staff-profile-container">
        <div className="profile-header">
          <h1>Multi-Location Access</h1>
          <p style={{ color: '#6b7280', marginTop: '8px' }}>
            Request access to work at multiple business locations
          </p>
        </div>

        {error && (
          <div style={{
            padding: '12px',
            marginBottom: '20px',
            background: '#fef2f2',
            border: '1px solid #fee2e2',
            borderRadius: '8px',
            color: '#dc2626'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            padding: '12px',
            marginBottom: '20px',
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            color: '#16a34a'
          }}>
            {success}
          </div>
        )}

        {/* Current Status Card */}
        <div className="profile-card">
          <div className="card-header">
            <h2>Current Status</h2>
          </div>
          <div className="card-body">
            {requestStatus === 'none' && (
              <div>
                <div style={{ 
                  display: 'inline-block',
                  padding: '6px 12px',
                  background: '#f3f4f6',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#6b7280'
                }}>
                  No Request Submitted
                </div>
                <p style={{ marginTop: '12px', color: '#6b7280' }}>
                  You currently have access to one location only. Request multi-location access below to work at other business locations.
                </p>
              </div>
            )}
            
            {requestStatus === 'pending' && (
              <div>
                <div style={{ 
                  display: 'inline-block',
                  padding: '6px 12px',
                  background: '#fef3c7',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#92400e'
                }}>
                  ⏳ Pending Review
                </div>
                <p style={{ marginTop: '12px', color: '#6b7280' }}>
                  Your multi-location request is being reviewed by your business owner. You'll be notified once it's approved or denied.
                </p>
              </div>
            )}
            
            {requestStatus === 'approved' && (
              <div>
                <div style={{ 
                  display: 'inline-block',
                  padding: '6px 12px',
                  background: '#d1fae5',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#065f46'
                }}>
                  ✓ Approved
                </div>
                <p style={{ marginTop: '12px', color: '#6b7280' }}>
                  You have multi-location access! You can now create schedules at your approved locations.
                </p>
                
                {approvedLocations && approvedLocations.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                      Approved Locations:
                    </h3>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {approvedLocations.map(location => (
                        <li key={location._id} style={{ marginBottom: '4px', color: '#374151' }}>
                          {location.businessName || location.name} {location.city && location.state ? `- ${location.city}, ${location.state}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            {requestStatus === 'denied' && (
              <div>
                <div style={{ 
                  display: 'inline-block',
                  padding: '6px 12px',
                  background: '#fee2e2',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#991b1b'
                }}>
                  ✗ Request Denied
                </div>
                <p style={{ marginTop: '12px', color: '#6b7280' }}>
                  Your previous multi-location request was not approved. You can submit a new request below if you'd like to try again.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Request Form - Only show if no pending request */}
        {(requestStatus === 'none' || requestStatus === 'denied') && (
          <div className="profile-card">
            <div className="card-header">
              <h2>Request Multi-Location Access</h2>
            </div>
            <div className="card-body">
              {/* Information Section - Top */}
              <div style={{
                padding: '16px',
                backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: '#1e40af' }}>
                  ℹ️ About Multi-Location Access
                </h3>
                <p style={{ fontSize: '14px', color: '#1e40af', marginBottom: '8px', lineHeight: '1.5' }}>
                  Multi-location access allows you to work at multiple business locations and manage your schedule across all approved locations.
                </p>
                <p style={{ fontSize: '14px', color: '#1e40af', marginBottom: '0', lineHeight: '1.5' }}>
                  <strong>Important:</strong> By requesting multi-location access, you acknowledge it is your responsibility to honor commitments at all locations and check your schedule regularly to avoid conflicts.
                </p>
              </div>

              {/* Acceptance Checkbox */}
              <div style={{
                padding: '16px',
                backgroundColor: '#fef3c7',
                border: '2px solid #fbbf24',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'start',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={hasAcceptedTerms}
                    onChange={(e) => setHasAcceptedTerms(e.target.checked)}
                    style={{
                      marginRight: '12px',
                      marginTop: '2px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ fontSize: '14px', color: '#92400e', fontWeight: '500' }}>
                    I understand and accept responsibility for managing my schedule across multiple locations, honoring all bookings, and avoiding scheduling conflicts.
                  </span>
                </label>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  fontWeight: '600', 
                  marginBottom: '8px',
                  fontSize: '14px'
                }}>
                  Select Locations *
                </label>
                {locations.length === 0 ? (
                  <p style={{ color: '#6b7280', fontStyle: 'italic' }}>
                    No additional locations available
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {locations.map(location => {
                      const isCurrentLocation = location._id === currentLocationId;
                      const isSelected = selectedLocations.includes(location._id);
                      
                      return (
                        <label
                          key={location._id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px',
                            border: isSelected 
                              ? '2px solid #00509d' 
                              : '1px solid #e5e7eb',
                            borderRadius: '8px',
                            cursor: isCurrentLocation ? 'not-allowed' : 'pointer',
                            background: isSelected 
                              ? '#eff6ff' 
                              : 'white',
                            opacity: isCurrentLocation ? 0.9 : 1,
                            transition: 'all 0.2s'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleLocationSelection(location._id)}
                            disabled={isCurrentLocation}
                            style={{ marginRight: '12px', cursor: isCurrentLocation ? 'not-allowed' : 'pointer' }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '500', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {location.businessName || location.name}
                              {isCurrentLocation && (
                                <span style={{
                                  fontSize: '11px',
                                  padding: '2px 8px',
                                  backgroundColor: '#dbeafe',
                                  color: '#1e40af',
                                  borderRadius: '4px',
                                  fontWeight: '600'
                                }}>
                                  CURRENT
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>
                              {location.address && `${location.address}, `}{location.city}, {location.state} {location.zip || location.zipCode || ''}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  fontWeight: '600', 
                  marginBottom: '8px',
                  fontSize: '14px'
                }}>
                  Note to Business Owner (Optional)
                </label>
                <textarea
                  value={requestNote}
                  onChange={(e) => setRequestNote(e.target.value)}
                  placeholder="Explain why you need multi-location access..."
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <button
                onClick={handleSubmitRequest}
                disabled={submitting || selectedLocations.length === 0 || !hasAcceptedTerms}
                style={{
                  padding: '12px 24px',
                  background: (selectedLocations.length > 0 && hasAcceptedTerms) ? '#00509d' : '#e5e7eb',
                  color: (selectedLocations.length > 0 && hasAcceptedTerms) ? 'white' : '#9ca3af',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: (selectedLocations.length > 0 && hasAcceptedTerms) ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s'
                }}
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
              {!hasAcceptedTerms && selectedLocations.length > 0 && (
                <p style={{ marginTop: '8px', color: '#dc2626', fontSize: '13px' }}>
                  Please accept the terms and responsibilities above to submit your request
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </StaffSidebarLayout>
  );
}

export default MultiLocationRequest;
